import { arrayRemove, arrayUnion, doc, getDoc, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";
import {
  PROFILE_THEMES,
  getProfileTheme,
  isThemeOwned,
  monthsActiveFromCreatedAt,
  themeMatchesAudience,
  displayProfileThemeId,
  dropsUnlockedFromUser,
} from "../constants/profileThemeCatalog";

const unlockProfileThemeFn = httpsCallable(functions, "unlockProfileTheme");
const applyProfileThemeFn = httpsCallable(functions, "applyProfileTheme");

export async function fetchThemeOwnership(userId) {
  if (!userId) {
    return {
      profileTheme: null,
      unlockedProfileThemes: [],
      credits: 0,
      subscriberCount: 0,
      monthsActive: 0,
      dropsUnlocked: 0,
      savedThemes: [],
    };
  }

  const [userSnap, perksSnap] = await Promise.all([
    getDoc(doc(db, "users", userId)),
    getDoc(doc(db, "userPerks", userId)),
  ]);

  const user = userSnap.exists() ? userSnap.data() : {};
  const perks = perksSnap.exists() ? perksSnap.data() : {};
  const unlocked = Array.isArray(perks.unlockedProfileThemes)
    ? [...perks.unlockedProfileThemes]
    : [];

  Object.values(PROFILE_THEMES).forEach((theme) => {
    if (
      theme.unlockType === "free"
      && themeMatchesAudience(theme, user)
      && !unlocked.includes(theme.id)
    ) {
      unlocked.push(theme.id);
    }
  });

  return {
    profileTheme: displayProfileThemeId(user.profileTheme || perks.profileTheme, user),
    unlockedProfileThemes: unlocked,
    credits: Number(perks.credits || 0),
    subscriberCount: Number(user.followerCount || user.subscriberCount || 0),
    monthsActive: monthsActiveFromCreatedAt(user.createdAt || user.creatorSince),
    dropsUnlocked: dropsUnlockedFromUser(user),
    savedThemes: Array.isArray(user.savedThemes)
      ? user.savedThemes.filter((id) => typeof id === "string" && PROFILE_THEMES[id])
      : [],
  };
}

export function evaluateThemeAccess(themeId, ownership) {
  const theme = getProfileTheme(themeId);
  const owned = isThemeOwned(theme, {
    unlockedIds: ownership.unlockedProfileThemes || [],
    subscriberCount: ownership.subscriberCount || 0,
    monthsActive: ownership.monthsActive || 0,
    dropsUnlocked: ownership.dropsUnlocked || 0,
  });
  return { theme, owned };
}

function callableErrorMessage(err) {
  const code = err?.code || "";
  const msg = err?.message || "";
  if (
    code === "functions/not-found"
    || code === "functions/unavailable"
    || /not-found|unavailable|Failed to fetch|INTERNAL/i.test(`${code} ${msg}`)
  ) {
    return "Theme service is unavailable. Deploy unlockProfileTheme / applyProfileTheme, then try again.";
  }
  return msg || code || "Request failed.";
}

/** Server-side credit / achievement unlock. No client-side credit writes. */
export async function unlockProfileTheme(themeId) {
  if (!PROFILE_THEMES[themeId]) throw new Error("Unknown theme.");
  try {
    const res = await unlockProfileThemeFn({ themeId });
    return res.data;
  } catch (err) {
    throw new Error(callableErrorMessage(err));
  }
}

/**
 * Apply an already-owned theme (no repeat cost).
 * Requires the applyProfileTheme callable — no local Firestore fallback.
 */
export async function applyProfileTheme(themeId) {
  if (!PROFILE_THEMES[themeId]) throw new Error("Unknown theme.");
  try {
    const res = await applyProfileThemeFn({ themeId });
    return res.data;
  } catch (err) {
    throw new Error(callableErrorMessage(err));
  }
}

/** Persist a catalog heart on users/{uid}.savedThemes (arrayUnion / arrayRemove). */
export async function toggleSavedTheme(userId, themeId, currentlySaved) {
  if (!userId) throw new Error("Not signed in.");
  if (!PROFILE_THEMES[themeId]) throw new Error("Unknown theme.");
  await updateDoc(doc(db, "users", userId), {
    savedThemes: currentlySaved ? arrayRemove(themeId) : arrayUnion(themeId),
  });
}
